import { c as _c } from "react/compiler-runtime";
import { FilterToolbarClearAll } from "@components/common/faro-table/faro-table-filter/filter-toolbar-clear-all";
import { FilterToggleButton } from "@components/common/faro-table/faro-table-filter/filter-toggle-button";
import { FilterToolbarQuickSearch, onClearTableSearch } from "@components/common/faro-table/faro-table-filter/filter-toolbar-quick-search";
import { SortSelector } from "@components/common/sort-selector";
import { IconButton, SearchIcon } from "@flat-ui";
import { QUICK_FILTER, useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui-wrapper";
import { useMarkupsViewQueryParams } from "@pages/project-details/project-markups/calendar/use-markups-view-query-params";
import { MARKUPS_SORT_OPTIONS } from "@pages/project-details/project-markups/markups-toolbar/markups-sort-utils";
import { MarkupsCount } from "@pages/project-details/project-markups/markups-toolbar/markups-count";
import { MarkupsViewControls } from "@pages/project-details/project-markups/markups-toolbar/markups-view-controls";
import { AnnotationsToolbarEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { Fragment, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Search of the annotations on small screens: an icon button, with the search field opening
 * underneath the toolbar row, like the other lists
 */
function useMarkupsMobileSearch() {
  const $ = _c(14);
  const [searchParams] = useSearchParams();
  const searchRef = useRef(null);
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => (searchParams.get(QUICK_FILTER) ?? "") !== "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [isSearchOpen, setIsSearchOpen] = useState(t0);
  let t1;
  if ($[2] !== isSearchOpen) {
    t1 = function toggleSearch() {
      if (isSearchOpen) {
        onClearTableSearch.emit();
      } else {
        requestAnimationFrame(() => searchRef.current?.querySelector("input")?.focus());
      }
      setIsSearchOpen(!isSearchOpen);
    };
    $[2] = isSearchOpen;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const toggleSearch = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SearchIcon />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isSearchOpen || $[6] !== toggleSearch) {
    t3 = <IconButton size="s" aria-label="Search" aria-expanded={isSearchOpen} pressed={isSearchOpen} onClick={toggleSearch}>{t2}</IconButton>;
    $[5] = isSearchOpen;
    $[6] = toggleSearch;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = !isSearchOpen;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FilterToolbarQuickSearch />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <Box ref={searchRef} hidden={t4}>{t5}</Box>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t6) {
    t7 = {
      searchButton: t3,
      searchField: t6
    };
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
/** Number of the shown annotations and their export, like the other lists show on the right */
function MarkupsCountAndExport(t0) {
  const $ = _c(7);
  const {
    shownCount,
    totalCount,
    isLoading,
    exportComponent
  } = t0;
  if (isLoading || shownCount === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      gap: 2
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== shownCount || $[2] !== totalCount) {
    t2 = <MarkupsCount shownCount={shownCount} totalCount={totalCount} />;
    $[1] = shownCount;
    $[2] = totalCount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== exportComponent || $[5] !== t2) {
    t3 = <Stack direction="row" sx={t1}>{t2}{exportComponent}</Stack>;
    $[4] = exportComponent;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
/**
 * Toolbar of the annotations tab, shared by the table, calendar and board, so the search,
 * sort and filters stay as they are when switching between them. The view switch is on its own
 * row on top where the project buttons share the row of the tabs, and next to them otherwise.
 */
export function MarkupsToolbar(t0) {
  const $ = _c(54);
  let countAndExportProps;
  let filterChips;
  let onSortItemChange;
  let sortItem;
  if ($[0] !== t0) {
    ({
      filterChips,
      sortItem,
      onSortItemChange,
      ...countAndExportProps
    } = t0);
    $[0] = t0;
    $[1] = countAndExportProps;
    $[2] = filterChips;
    $[3] = onSortItemChange;
    $[4] = sortItem;
  } else {
    countAndExportProps = $[1];
    filterChips = $[2];
    onSortItemChange = $[3];
    sortItem = $[4];
  }
  const {
    isAnyFilterApplied
  } = useFilterQueryParams();
  const {
    viewMode
  } = useMarkupsViewQueryParams();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    searchButton,
    searchField
  } = useMarkupsMobileSearch();
  const {
    trackEvent
  } = useTrackEvent();
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(isAnyFilterApplied);
  let t1;
  if ($[5] !== isFilterBarOpen || $[6] !== trackEvent || $[7] !== viewMode) {
    t1 = function toggleFilterBar() {
      setIsFilterBarOpen(!isFilterBarOpen);
      trackEvent({
        name: AnnotationsToolbarEvents.toggleFilters,
        props: {
          isOpen: !isFilterBarOpen,
          viewMode
        }
      });
    };
    $[5] = isFilterBarOpen;
    $[6] = trackEvent;
    $[7] = viewMode;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const toggleFilterBar = t1;
  let t2;
  if ($[9] !== onSortItemChange || $[10] !== sortItem || $[11] !== viewMode) {
    t2 = <SortSelector sortOptions={MARKUPS_SORT_OPTIONS} sortItem={sortItem} setSortItem={onSortItemChange} subjectType="annotations" trackingViewMode={viewMode} />;
    $[9] = onSortItemChange;
    $[10] = sortItem;
    $[11] = viewMode;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const sortSelector = t2;
  let t3;
  if ($[13] !== filterChips || $[14] !== isFilterBarOpen) {
    t3 = isFilterBarOpen && <Stack direction="row" sx={{
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1
    }}>{Object.values(filterChips).filter(_temp).map(_temp2)}<FilterToolbarClearAll /></Stack>;
    $[13] = filterChips;
    $[14] = isFilterBarOpen;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const filterChipsBar = t3;
  if (isScreenSmOrSmaller) {
    let t4;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <MarkupsViewControls placement="ownRow" />;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        mt: 1.5,
        mb: 1,
        gap: 1
      };
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    let t6;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        alignItems: "center",
        gap: 0.5
      };
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== isFilterBarOpen || $[20] !== toggleFilterBar) {
      t7 = <FilterToggleButton isIconOnly={true} isOpen={isFilterBarOpen} onToggle={toggleFilterBar} />;
      $[19] = isFilterBarOpen;
      $[20] = toggleFilterBar;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        ml: "auto"
      };
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== countAndExportProps) {
      t9 = <Box sx={t8}><MarkupsCountAndExport {...countAndExportProps} /></Box>;
      $[23] = countAndExportProps;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== searchButton || $[26] !== sortSelector || $[27] !== t7 || $[28] !== t9) {
      t10 = <Stack direction="row" sx={t6}>{searchButton}{t7}{sortSelector}{t9}</Stack>;
      $[25] = searchButton;
      $[26] = sortSelector;
      $[27] = t7;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    let t11;
    if ($[30] !== filterChipsBar || $[31] !== searchField || $[32] !== t10) {
      t11 = <Stack>{t4}<Stack sx={t5}>{t10}{searchField}{filterChipsBar}</Stack></Stack>;
      $[30] = filterChipsBar;
      $[31] = searchField;
      $[32] = t10;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    return t11;
  }
  let t4;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MarkupsViewControls placement="ownRow" />;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  let t5;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      mt: 1.5,
      mb: 1,
      gap: 1
    };
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1
    };
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  let t7;
  let t8;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1
    };
    t8 = <FilterToolbarQuickSearch />;
    $[37] = t7;
    $[38] = t8;
  } else {
    t7 = $[37];
    t8 = $[38];
  }
  let t9;
  if ($[39] !== isFilterBarOpen || $[40] !== toggleFilterBar) {
    t9 = <FilterToggleButton isOpen={isFilterBarOpen} onToggle={toggleFilterBar} />;
    $[39] = isFilterBarOpen;
    $[40] = toggleFilterBar;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  let t10;
  if ($[42] !== sortSelector || $[43] !== t9) {
    t10 = <Stack direction="row" sx={t7}>{t8}{t9}{sortSelector}</Stack>;
    $[42] = sortSelector;
    $[43] = t9;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      ml: "auto"
    };
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== countAndExportProps) {
    t12 = <Box sx={t11}><MarkupsCountAndExport {...countAndExportProps} /></Box>;
    $[46] = countAndExportProps;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t10 || $[49] !== t12) {
    t13 = <Stack direction="row" sx={t6}>{t10}{t12}</Stack>;
    $[48] = t10;
    $[49] = t12;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== filterChipsBar || $[52] !== t13) {
    t14 = <Stack>{t4}<Stack sx={t5}>{t13}{filterChipsBar}</Stack></Stack>;
    $[51] = filterChipsBar;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  return t14;
}
function _temp2(filterChip_0, index) {
  return <Fragment key={index}>{filterChip_0}</Fragment>;
}
function _temp(filterChip) {
  return filterChip !== null;
}